import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  BookOpen,
  Check,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  Info,
  LogOut,
  MessageSquare,
  Shield,
  Trash2,
  User,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import {
  deleteAccount,
  getUserSettings,
  logout,
  updateUserSettings,
} from "@/lib/api";
import { haptic } from "@/lib/haptic";
import Tutorial from "@/components/Tutorial";
import type { UserSettings } from "@/types";

type Section = "username" | "day_start" | "delete_account" | null;

export default function Settings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<Section>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [formError, setFormError] = useState("");

  const [newUsername, setNewUsername] = useState("");
  const [dayStartHour, setDayStartHour] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [showTutorial, setShowTutorial] = useState(false);
  const [muteAudio, setMuteAudio] = useState<boolean>(() =>
    typeof window !== "undefined" && localStorage.getItem("locomoco_mute_audio") === "1",
  );

  useEffect(() => {
    async function load() {
      const data = await getUserSettings();
      if (data) {
        setSettings(data);
        setNewUsername(data.username ?? "");
        setDayStartHour(data.day_start_hour ?? 0);
      }
      setLoading(false);
    }
    load();
  }, [navigate]);

  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((msg: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(msg);
    toastTimerRef.current = setTimeout(() => setToast(""), 3000);
  }, []);

  useEffect(
    () => () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    },
    [],
  );

  const openSection = (section: Section) => {
    setFormError("");
    setActiveSection(section);
  };

  const handleSaveUsername = async () => {
    if (!newUsername.trim()) {
      setFormError("ユーザー名を入力してください");
      return;
    }
    setSaving(true);
    const { error } = await updateUserSettings({ username: newUsername.trim() });
    setSaving(false);
    if (error) {
      setFormError(error);
      return;
    }
    setSettings((prev) => (prev ? { ...prev, username: newUsername.trim() } : prev));
    setActiveSection(null);
    showToast("ユーザー名を更新しました");
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "DELETE") {
      setFormError('「DELETE」と入力してください');
      return;
    }
    setSaving(true);
    try {
      await deleteAccount();
    } catch (e) {
      setSaving(false);
      setFormError(e instanceof Error ? e.message : "削除に失敗しました");
      return;
    }
    // 全消去後はリロードしてユーザー行を作り直す（App の initUser を再実行させる）
    window.location.replace("/");
  };

  const handleSaveDayStart = async () => {
    setSaving(true);
    const { error } = await updateUserSettings({ day_start_hour: dayStartHour });
    setSaving(false);
    if (error) {
      setFormError(error);
      return;
    }
    setSettings((prev) => (prev ? { ...prev, day_start_hour: dayStartHour } : prev));
    setActiveSection(null);
    showToast("区切り時刻を更新しました");
  };

  const handleToggleMute = () => {
    const next = !muteAudio;
    setMuteAudio(next);
    if (next) localStorage.setItem("locomoco_mute_audio", "1");
    else localStorage.removeItem("locomoco_mute_audio");
    showToast(next ? "音声を消音にしました" : "音声をオンにしました");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} />}
      <div
        className="bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-slate-100"
        style={{ minHeight: "100dvh" }}
      >
        <header className="pt-safe px-4 pb-3 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
          <button
            onClick={() => navigate("/")}
            className="p-2.5 rounded-full active:bg-slate-200 dark:active:bg-slate-800 transition-colors"
          >
            <ArrowLeft size={22} className="text-slate-600 dark:text-slate-400" />
          </button>
          <h1 className="text-lg font-black">設定</h1>
        </header>

        <AnimatePresence>
          {toast && (
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-emerald-600 text-white px-5 py-3 rounded-2xl shadow-xl flex items-center gap-2 text-sm font-bold whitespace-nowrap"
            >
              <Check size={16} />
              {toast}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
          <SettingGroup title="プロフィール">
            <SettingRow
              icon={<User size={18} />}
              label="ユーザー名"
              value={settings?.username || "未設定"}
              onClick={() => openSection("username")}
            />
          </SettingGroup>

          <SettingGroup title="学習設定">
            <SettingRow
              icon={<Clock size={18} />}
              label="1日の区切り時刻"
              value={`${String(settings?.day_start_hour ?? 0).padStart(2, "0")}:00`}
              onClick={() => openSection("day_start")}
              description="この時刻以前は前日として扱われます"
            />
            <div className="flex items-center justify-between px-4 py-3.5">
              <div className="flex items-center gap-3">
                <span
                  className={`p-1.5 rounded-xl ${
                    muteAudio
                      ? "bg-slate-100 dark:bg-slate-800 text-slate-400"
                      : "bg-blue-50 dark:bg-blue-950/40 text-blue-500"
                  }`}
                >
                  {muteAudio ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    音声読み上げ
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">自動再生・タップ再生の両方に適用</p>
                </div>
              </div>
              <button
                onClick={handleToggleMute}
                className={`relative rounded-full transition-colors duration-200 focus:outline-none ${
                  muteAudio ? "bg-slate-300 dark:bg-slate-700" : "bg-blue-500"
                }`}
                style={{ height: "26px", width: "46px" }}
              >
                <span
                  className={`absolute top-0.5 left-0.5 rounded-full bg-white shadow transition-transform duration-200 ${
                    muteAudio ? "translate-x-5" : "translate-x-0"
                  }`}
                  style={{ width: "22px", height: "22px" }}
                />
              </button>
            </div>
          </SettingGroup>

          <SettingGroup title="法的情報">
            <SettingRow
              icon={<FileText size={18} />}
              label="利用規約"
              value=""
              onClick={() => navigate("/terms")}
              rightIcon={<ExternalLink size={14} className="text-slate-300" />}
            />
            <SettingRow
              icon={<Shield size={18} />}
              label="プライバシーポリシー"
              value=""
              onClick={() => navigate("/privacy")}
              rightIcon={<ExternalLink size={14} className="text-slate-300" />}
            />
          </SettingGroup>

          <SettingGroup title="アプリ情報">
            <SettingRow
              icon={<BookOpen size={18} />}
              label="チュートリアルを見る"
              value=""
              onClick={() => setShowTutorial(true)}
              rightIcon={<ChevronRight size={16} className="text-slate-300" />}
            />
            <SettingRow
              icon={<MessageSquare size={18} />}
              label="お問い合わせ"
              value=""
              onClick={() =>
                window.open("mailto:vetements0720@gmail.com?subject=locomoco お問い合わせ", "_blank")
              }
              rightIcon={<ExternalLink size={14} className="text-slate-300" />}
            />
            <SettingRow
              icon={<Info size={18} />}
              label="バージョン"
              value="1.0.0"
              onClick={() => {}}
              disabled
            />
          </SettingGroup>

          <button
            onClick={async () => {
              await logout();
              // ログイン画面まで作り直したいので replace ではなく再読み込み
              window.location.href = "/";
            }}
            className="w-full py-3 rounded-2xl border border-slate-200 dark:border-slate-700 text-sm font-bold active:bg-slate-100 dark:active:bg-slate-800 transition-colors flex items-center justify-center gap-1.5"
          >
            <LogOut size={14} />
            ログアウト
          </button>

          <button
            onClick={() => openSection("delete_account")}
            className="w-full py-3 rounded-2xl text-slate-400 text-sm font-medium active:text-red-500 transition-colors flex items-center justify-center gap-1.5"
          >
            <Trash2 size={14} />
            アカウントを削除する
          </button>
        </div>

        <AnimatePresence>
          {activeSection && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/50"
              onClick={() => setActiveSection(null)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {activeSection && (
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl p-6 pb-safe max-w-lg mx-auto"
            >
              <div className="w-10 h-1 bg-slate-200 dark:bg-slate-700 rounded-full mx-auto mb-5" />

              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-black">
                  {activeSection === "username" && "ユーザー名を変更"}
                  {activeSection === "day_start" && "1日の区切り時刻"}
                  {activeSection === "delete_account" && "アカウントを削除"}
                </h2>
                <button
                  onClick={() => setActiveSection(null)}
                  className="p-2 rounded-full active:bg-slate-100 dark:active:bg-slate-800"
                >
                  <X size={20} className="text-slate-500" />
                </button>
              </div>

              {formError && (
                <p className="text-red-500 text-sm mb-4 bg-red-50 dark:bg-red-950/30 rounded-xl px-4 py-2.5">
                  {formError}
                </p>
              )}

              {activeSection === "username" && (
                <div className="space-y-4">
                  <StyledInput
                    type="text"
                    value={newUsername}
                    onChange={setNewUsername}
                    placeholder="表示名"
                    autoFocus
                  />
                  <SaveButton onClick={handleSaveUsername} loading={saving} />
                </div>
              )}

              {activeSection === "delete_account" && (
                <div className="space-y-4">
                  <div className="bg-red-50 dark:bg-red-950/30 rounded-2xl p-4">
                    <p className="text-red-600 dark:text-red-400 font-bold text-sm mb-1">
                      ⚠️ この操作は取り消せません
                    </p>
                    <p className="text-red-500/80 text-xs leading-relaxed">
                      アカウントを削除すると、すべての学習データ・進捗・ストリークが完全に削除されます。
                    </p>
                  </div>
                  <StyledInput
                    type="text"
                    value={deleteConfirm}
                    onChange={setDeleteConfirm}
                    placeholder='確認のため「DELETE」と入力'
                    autoFocus
                  />
                  <button
                    onClick={handleDeleteAccount}
                    disabled={saving || deleteConfirm !== "DELETE"}
                    className="w-full py-4 bg-red-600 text-white font-bold rounded-2xl text-base active:scale-[0.98] transition-transform disabled:opacity-40 flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Trash2 size={18} />
                        アカウントを削除する
                      </>
                    )}
                  </button>
                </div>
              )}

              {activeSection === "day_start" && (
                <div className="space-y-5">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    この時刻を1日の始まりとします。深夜2時に設定すると、深夜2時以降が「今日」として扱われます。
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {[0, 1, 2, 3, 4, 5, 6].map((h) => (
                      <button
                        key={h}
                        onClick={() => setDayStartHour(h)}
                        className={`py-3.5 rounded-xl font-bold text-sm transition-all active:scale-95 ${
                          dayStartHour === h
                            ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                        }`}
                      >
                        {String(h).padStart(2, "0")}:00
                      </button>
                    ))}
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-950/30 rounded-2xl px-4 py-3">
                    <p className="text-blue-600 dark:text-blue-400 font-bold text-sm">
                      設定中: {String(dayStartHour).padStart(2, "0")}:00 〜 翌
                      {String(dayStartHour).padStart(2, "0")}:00
                    </p>
                  </div>
                  <SaveButton onClick={handleSaveDayStart} loading={saving} />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

function SettingGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] mb-2 px-1">
        {title}
      </h3>
      <div className="bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
        {children}
      </div>
    </div>
  );
}

function SettingRow({
  icon,
  label,
  value,
  onClick,
  description,
  disabled,
  rightIcon,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onClick: () => void;
  description?: string;
  disabled?: boolean;
  rightIcon?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-4 px-5 py-4 active:bg-slate-50 dark:active:bg-slate-800/50 transition-colors border-b border-slate-100 dark:border-slate-800 last:border-0 text-left disabled:opacity-60 disabled:cursor-default"
    >
      <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400 shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{label}</p>
        {description && <p className="text-xs text-slate-400 mt-0.5">{description}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {value && (
          <span className="text-sm text-slate-400 dark:text-slate-500 max-w-[140px] truncate">
            {value}
          </span>
        )}
        {rightIcon ??
          (!disabled && <ChevronRight size={16} className="text-slate-300 dark:text-slate-600" />)}
      </div>
    </button>
  );
}

function StyledInput({
  type,
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      className="w-full px-4 py-3.5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
    />
  );
}

function SaveButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="w-full py-4 bg-blue-600 text-white font-bold rounded-2xl text-base active:scale-[0.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
    >
      {loading ? (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
      ) : (
        <>
          <Check size={18} />
          保存する
        </>
      )}
    </button>
  );
}
